// src/components/forms/formSteps/PhoneStep.tsx
import React from "react";
import { Box, Typography, TextField, Button } from "@mui/material";

interface PhoneStepProps {
  phone: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void; // Added explicit submit handler
  isSubmitting: boolean;
  isMobile: boolean;
}

const PhoneStep: React.FC<PhoneStepProps> = ({
  phone,
  validation,
  onChange,
  onSubmit,
  isSubmitting,
  isMobile,
}) => {
  // Handle keydown event - only prevent default
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <>
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        9. What is your cell number? For us to call you about compensation
        please be ready to pick up <span style={{ color: "red" }}>*</span>
      </Typography>

      <TextField
        fullWidth
        placeholder="Enter your phone number"
        value={phone}
        onChange={(e) => {
          const newValue = e.target.value;
          // Only allow digits and limit to 10 characters
          const digitsOnly = newValue.replace(/\D/g, "");
          if (digitsOnly.length <= 10) {
            onChange("phone", digitsOnly);
          }
        }}
        onKeyDown={handleKeyDown}
        error={!validation.valid}
        helperText={validation.error || "Format:  XXX XXX XXXX (10 digits)"}
        inputProps={{
          maxLength: 10,
          inputMode: "numeric",
          pattern: "[0-9]*",
        }}
        sx={{ mb: 2 }}
      />

      {/* Mobile submit button placement */}
      {isMobile && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            id="SubmitForm"
            size="large"
            onClick={onSubmit}
            disabled={phone.trim() === "" || !validation.valid || isSubmitting}
            sx={{
              minWidth: "200px",
              py: 1.5,
              fontWeight: "bold",
              backgroundColor: "#ff6b35",
              "&:hover": {
                backgroundColor: "#e25a2a",
              },
            }}
          >
            Submit
          </Button>
        </Box>
      )}

      {/* TCPA text */}
      <div
        style={{
          textAlign: "center",
          fontSize: isMobile ? "0.6rem" : "0.8rem",
        }}
      >
        <label data-tf-element-role="consent-language">
          <div>
            By clicking "Submit and Get Free Consultation" you expressly consent
            to receive calls and text messages for marketing purposes from{" "}
            <span data-tf-element-role="consent-advertiser-name">
              Car Accident Counsel
            </span>{" "}
            our affiliates, and marketing{" "}
            <a href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/">
              partners
            </a>{" "}
            using automated technology including artificial pre-recorded voice.
            You agree that we may contact you at any time, including before 8am
            or after 9pm local time. Consent is not a condition of purchase. You
            agree to the use of electronic signatures, our{" "}
            <a href="https://www.caraccidentcounsel.com/privacy-policy/">
              Privacy Policy
            </a>
            , and{" "}
            <a href="https://www.caraccidentcounsel.com/terms-of-service/">
              Terms of Use
            </a>
            .<span className="tcpa-required">*</span>
          </div>
        </label>
      </div>

      {/* Desktop submit button placement */}
      {!isMobile && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            id="SubmitForm"
            size="large"
            onClick={onSubmit}
            disabled={phone.trim() === "" || !validation.valid || isSubmitting}
            sx={{
              minWidth: "200px",
              py: 1.5,
              fontWeight: "bold",
              backgroundColor: "#ff6b35",
              "&:hover": {
                backgroundColor: "#e25a2a",
              },
            }}
          >
            Submit
          </Button>
        </Box>
      )}
    </>
  );
};

export default PhoneStep;
